import React, { useEffect, useMemo, useState } from "react";
import CoverageStrip from "./CoverageStrip";
import {
  getDate,
  calculateCoverage,
  createMaterials,
  getMaterial,
  formatDate,
  formatQuantity,
} from "../../utils/reportUtils";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";

const Shortages = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [results, setResults] = useState([]);
  const [q, setQ] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { getReports } = useOutletContext();
  const dashboardStatus = location.state?.status;
  const [statusOn, setStatusOn] = useState({
    SHORT: dashboardStatus === "SHORT" || !dashboardStatus,
    AT_RISK: dashboardStatus === "AT_RISK" || !dashboardStatus,
    COVERED: dashboardStatus === "COVERED",
  });
  const [plant, setPlant] = useState("");
  const [sort, setSort] = useState({ k: "urgency", d: 1 });
  

  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());

  useEffect(() => {
    loadShortages();
  }, []);

  const loadShortages = async () => {
    setLoading(true);
    setError(null);
    try {
      // Same reports as the Dashboard, reused from its last load (loaded once if there is none yet).
      const { m, s, p, o, prd, alt, d, u } = await getReports(false);
      const mrpData = m.data || [];
      const stockData = s.data || [];
      const prData = p.data || [];
      const poData = o.data || [];
      const productionData = prd.data || [];

      setMaterials(
        createMaterials(mrpData, stockData, prData, poData, productionData),
      );
      setResults(
        calculateCoverage(
          mrpData,
          stockData,
          prData,
          poData,
          today,
          productionData,
          alt.data || [],
          d.data || [],
          u.data?.uploadDate || u.data?.createdDate,
          u.data?.uploadDate || u.data?.createdDate,
        ),
      );
    } catch (e) {
      setError(
        e.response?.data?.Exception ||
          e.response?.data?.message ||
          "Could not load shortages.",
      );
    } finally {
      setLoading(false);
    }
  };

  const plants = useMemo(
    () =>
      Array.from(new Set(materials.map((m) => m.plant).filter(Boolean))).sort(),
    [materials],
  );

  const toggleStatus = (k) =>
    setStatusOn((prev) => ({ ...prev, [k]: !prev[k] }));

  const doSort = (k) =>
    setSort((prev) => (prev.k === k ? { k, d: -prev.d } : { k, d: 1 }));

  const sortIcon = (k) =>
    sort.k === k ? <span className="ar">{sort.d > 0 ? "↑" : "↓"}</span> : null;

  const accessors = {
    urgency: (r) => r.firstShortageDate || "9999-99-99",
    code: (r) => getMaterial(materials, r.key)?.code || "",
    desc: (r) => getMaterial(materials, r.key)?.desc || "",
    shortage: (r) => r.shortageQty,
    req: (r) => r.totalRequirement,
  };

  const rows = useMemo(() => {
    let list = results.filter((r) => statusOn[r.status]);

    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((r) => {
        const m = getMaterial(materials, r.key);
        return `${m?.code || ""} ${m?.desc || ""} ${m?.materialGroup || ""}`
          .toLowerCase()
          .includes(needle);
      });
    }
    if (plant) {
      list = list.filter((r) => getMaterial(materials, r.key)?.plant === plant);
    }

    const acc = accessors[sort.k];
    list = [...list].sort((a, b) => {
      const av = acc(a),
        bv = acc(b);
      return av < bv ? -sort.d : av > bv ? sort.d : 0;
    });
    return list;
  }, [results, statusOn, q, plant, sort, materials]);

  const exportCsv = () => {
    const header = [
      "Material",
      "Description",
      "Requirement",
      "Supply",
      "Shortage",
      "First shortage",
      "UOM",
      "Plant",
    ];
    const lines = rows.map((r) => {
      const m = getMaterial(materials, r.key);
      return [
        m?.code,
        m?.desc,
        r.totalRequirement,
        r.totalSupply,
        r.shortageQty,
        r.firstShortageDate || "",
        m?.uom,
        m?.plant,
      ]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",");
    });
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "shortages.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading)
    return (
      <div className="page">
        <div className="empty">Loading shortages…</div>
      </div>
    );
  if (error)
    return (
      <div className="page">
        <div className="note err">
          <b>Couldn't load data.</b> {error}
        </div>
      </div>
    );

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Shortage workbench</h1>
          <p>
            <strong>{rows.length}</strong> of {results.length} materials shown ·
            sorted by first shortage date
          </p>
        </div>
        <button className="btn btn-o" onClick={exportCsv}>
          Export to Excel
        </button>
      </div>

      <div className="ctl">
        <div className="search">
          <input
            placeholder="Search material code, description or group"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="chips">
          <button
            className={`chip b-short${statusOn.SHORT ? " on" : ""}`}
            onClick={() => toggleStatus("SHORT")}
          >
            Short
          </button>
          <button
            className={`chip b-risk${statusOn.AT_RISK ? " on" : ""}`}
            onClick={() => toggleStatus("AT_RISK")}
          >
            At risk
          </button>
          <button
            className={`chip b-ok${statusOn.COVERED ? " on" : ""}`}
            onClick={() => toggleStatus("COVERED")}
          >
            Covered
          </button>
        </div>
        {/* <select
          className="sel"
          value={plant}
          onChange={(e) => setPlant(e.target.value)}
        >
          <option value="">All plants</option>
          {plants.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select> */}
      </div>

      <div className="card">
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th>Status</th>
                <th className="srt" onClick={() => doSort("code")}>
                  Material{sortIcon("code")}
                </th>
                <th className="srt" onClick={() => doSort("desc")}>
                  Description{sortIcon("desc")}
                </th>
                <th>10-week coverage</th>
                <th className="srt r" onClick={() => doSort("req")}>
                  Requirement{sortIcon("req")}
                </th>
                <th className="r">Supply</th>
                <th className="srt r" onClick={() => doSort("shortage")}>
                  Shortage{sortIcon("shortage")}
                </th>
                <th className="srt" onClick={() => doSort("urgency")}>
                  First shortage{sortIcon("urgency")}
                </th>
                <th>UOM</th>
                <th>Plant</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((r) => {
                  const m = getMaterial(materials, r.key);
                  const d = r.firstShortageDate
                    ? Math.round(
                        (Date.parse(r.firstShortageDate) - Date.parse(today)) /
                          86400000,
                      )
                    : null;
                  return (
                    <tr
                      key={r.key}
                      onClick={() => navigate(`../shortages/${r.key}`)}
                      className="clk"
                    >
                      <td>
                        <span
                          className={`bg ${r.status === "SHORT" ? "b-short" : r.status === "AT_RISK" ? "b-risk" : "b-ok"}`}
                        >
                          {r.status === "SHORT"
                            ? "Short"
                            : r.status === "AT_RISK"
                              ? "At risk"
                              : "Covered"}
                        </span>
                      </td>
                      <td className="mono">{m?.code || r.key}</td>
                      <td>
                        {m?.desc || "—"}
                        <div className="dim" style={{ fontSize: 11 }}>
                          {m?.materialGroup}
                        </div>
                      </td>
                      <td>
                        <CoverageStrip result={r} today={today} />
                      </td>
                      <td className="num">
                        {formatQuantity(r.totalRequirement)}
                      </td>
                      <td className="num">{formatQuantity(r.totalSupply)}</td>
                      <td
                        className={`num ${r.shortageQty > 0 ? "neg" : "dim"}`}
                      >
                        {r.shortageQty > 0
                          ? formatQuantity(r.shortageQty)
                          : "—"}
                      </td>
                      <td>
                        {r.firstShortageDate ? (
                          <>
                            {formatDate(r.firstShortageDate)}
                            <div className="dim" style={{ fontSize: 11 }}>
                              {d <= 0 ? "already short" : `in ${d} days`}
                            </div>
                          </>
                        ) : (
                          <span className="dim">—</span>
                        )}
                      </td>
                      <td className="dim">{m?.uom}</td>
                      <td>{m?.plant}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="empty">
                    <strong>Nothing matches these filters.</strong>Clear the
                    search, or switch a status chip back on.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Shortages;
